import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import CommonFormLayout from "../OptmisedCode/Form/CommonFormLayout";
import RolePermissionEditor from "./RolePermissionEditor";
import {
  fetchRolePermissionCatalog,
  fetchRolePermissions,
  modulesForApplication,
} from "./rolePermissionShared";

const BASE_ROUTE = "/role-permissions/access";

export default function RolePermissionsView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [applicationName, setApplicationName] = useState("");
  const [applicationId, setApplicationId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});

  const modules = useMemo(
    () => modulesForApplication(catalog || {}, applicationId),
    [catalog, applicationId]
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [cat, roleData] = await Promise.all([
          fetchRolePermissionCatalog(),
          fetchRolePermissions(id),
        ]);
        setCatalog(cat);
        setRoleName(roleData.user_role_name || "Role");
        setPermissions(
          (roleData.permissions || []).map((p) => ({
            application_module_action_id: p.application_module_action_id,
            permission_allowed: Boolean(p.permission_allowed),
          }))
        );

        const appId = findApplicationIdForRole(cat, Number(id));
        setApplicationId(appId);
        const app = (cat.applications || []).find(
          (a) => a.application_id === appId
        );
        setApplicationName(app?.application_name || "");
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            "Failed to load permissions."
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    const expanded = {};
    modules.forEach((m) => {
      expanded[m.application_module_id || m.module_id] = true;
    });
    setExpandedModules(expanded);
  }, [modules]);

  return (
    <CommonFormLayout
      mode="view"
      loading={loading}
      size="wide"
      wrapInGrid={false}
      hideFooter
      footerContent={
        <>
          <button className="ems-btn ems-ghost" onClick={() => navigate(BASE_ROUTE)}>
            Close
          </button>
          <button
            className="ems-btn ems-primary"
            onClick={() => navigate(`${BASE_ROUTE}/edit/${id}`)}
          >
            Edit
          </button>
        </>
      }
      headerProps={{
        addMessage: "Role permissions",
        updateMessage: "Role permissions",
        homeLink: BASE_ROUTE,
        homeText: "Role permissions",
      }}
    >
      <Typography variant="h6" gutterBottom>
        {roleName}
      </Typography>
      {applicationName && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Application: {applicationName}
        </Typography>
      )}

      {!permissions.length ? (
        <Alert severity="info">No permissions assigned to this role.</Alert>
      ) : (
        <RolePermissionEditor
          modules={modules}
          permissions={permissions}
          expandedModules={expandedModules}
          onExpandedModulesChange={setExpandedModules}
          readOnly
        />
      )}
    </CommonFormLayout>
  );
}

function findApplicationIdForRole(catalog, roleId) {
  const apps = catalog?.applications || [];
  const rolesByApplication = catalog?.roles_by_application || {};
  for (const app of apps) {
    const appId = app.application_id;
    const roles =
      rolesByApplication[appId] || rolesByApplication[String(appId)] || [];
    if (roles.some((r) => r.application_user_role_id === roleId)) {
      return appId;
    }
  }
  return null;
}
